import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { getCookie, getStorage, setStorage } from "./utils";
import { api } from "./api";
import i18n from "./i18n/config";

export type PlayerLobbyData = {
  points: number;
  is_ready: boolean;
  name: string;
};

export type LobbyState = {
  players: Record<string, PlayerLobbyData>;
  host_id: string;
};

type AppStore = {
  lang: string;
  name: string;
  id: string;
  isDark: boolean;
  setLanguage: (lang: string) => void;
  updateName: (name: string) => Promise<void>;
  toggleTheme: () => void;
  restartGame: () => { action: "restart_game" };
  kickPlayer: (playerId: string) => { action: string; payload?: unknown };
};

const SUPPORTED_LANGS = ["en", "de", "es", "fr", "sl"];

function detectLanguage(): string {
  const stored = getStorage("lang");
  if (stored) return stored;

  const browserLangs = navigator.languages ?? [navigator.language];
  for (const bl of browserLangs) {
    const code = bl.split("-")[0].toLowerCase();
    if (SUPPORTED_LANGS.includes(code)) return code;
  }
  return "en";
}

const detectedLang = detectLanguage();
i18n.changeLanguage(detectedLang);

export const useAppStore = create<AppStore>()(
  immer((set) => ({
    lang: detectedLang,
    name: getCookie("user") as string,
    id: getCookie("id") as string,
    isDark: getStorage("isDark") !== "false",
    setLanguage: (lang: string) => {
      setStorage("lang", lang);
      i18n.changeLanguage(lang);
      set((state) => void (state.lang = lang));
    },
    updateName: async (name: string) => {
      await api.updateName(name);
      set((state) => void (state.name = name));
    },
    toggleTheme: () => {
      set((state) => {
        state.isDark = !state.isDark;
        setStorage("isDark", String(state.isDark));
      });
    },
    //TODO: create lobbyState and move this into lobbyState
    restartGame: () => {
      return {
        action: "restart_game",
      };
    },
    kickPlayer: (playerId: string) => {
      return {
        action: "kick_player",
        payload: { id: playerId },
      };
    },
  })),
);
